import React, { useState, useRef, createElement } from 'react';
import { createPortal } from 'react-dom';
import { XIcon, MailIcon, SmartphoneIcon, LinkIcon, CheckIcon, UserIcon, EyeOffIcon, SendIcon, DollarSignIcon, QrCodeIcon, DownloadIcon, CalendarIcon, PercentIcon, BuildingIcon, ArrowRightIcon, ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { Recipient, Supplier, Installment } from './InstallmentPayment';
import { QRCodeSVG } from 'qrcode.react';
interface ShareLinkModalProps {
  onClose: () => void;
  splitType?: 'dates' | 'recipients' | 'commission' | null;
  recipients?: Recipient[];
  installments?: Installment[];
  suppliers?: Supplier[];
  totalAmount?: string;
  commissionType?: 'percentage' | 'fixed';
}
export function ShareLinkModal({
  onClose,
  splitType = null,
  recipients = [],
  installments = [],
  suppliers = [],
  totalAmount = '0',
  commissionType = 'percentage'
}: ShareLinkModalProps) {
  const [step, setStep] = useState<'preview' | 'share'>(() => {
    // Recipients mode goes straight to share (multi-recipient view)
    // No splitType (standard) also goes straight to share
    if (splitType === 'recipients' || !splitType) return 'share';
    // Schedule and commission start with preview
    return 'preview';
  });
  const [selectedOption, setSelectedOption] = useState<'email' | 'sms' | 'copy' | 'qrcode' | null>(null);
  const [recipientInput, setRecipientInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [qrDownloaded, setQrDownloaded] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);
  // For multiple recipients
  const [recipientContactMethods, setRecipientContactMethods] = useState<Record<number, 'email' | 'sms' | 'both'>>({});
  const [copiedRecipientId, setCopiedRecipientId] = useState<number | null>(null);
  const [recipientsViewTotalSettings, setRecipientsViewTotalSettings] = useState<Record<number, boolean>>(() => {
    const initialSettings: Record<number, boolean> = {};
    recipients.forEach(r => {
      initialSettings[r.id] = false;
    });
    return initialSettings;
  });
  const hasMultipleRecipients = splitType === 'recipients' && recipients.length > 0;
  const handleCopyLink = (recipientId?: number) => {
    const linkToCopy = recipientId ? `https://payment.example.com/pay/${recipientId}-abc123${recipientsViewTotalSettings[recipientId] ? '?showTotal=true' : ''}` : 'https://payment.example.com/pay/abc123';
    navigator.clipboard.writeText(linkToCopy);
    if (recipientId) {
      setCopiedRecipientId(recipientId);
      setTimeout(() => setCopiedRecipientId(null), 2000);
    } else {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  const handleDownloadQR = () => {
    if (!qrCodeRef.current) return;
    const canvas = qrCodeRef.current.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'payment-qrcode.png';
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setQrDownloaded(true);
    setTimeout(() => setQrDownloaded(false), 2000);
  };
  const handleSend = () => {
    onClose();
  };
  const getContactMethod = (recipient: Recipient): 'email' | 'sms' | 'both' => {
    if (recipientContactMethods[recipient.id]) {
      return recipientContactMethods[recipient.id];
    }
    if (recipient.email && recipient.phone) return 'both';
    if (recipient.email) return 'email';
    if (recipient.phone) return 'sms';
    return 'email';
  };
  const setContactMethod = (recipientId: number, method: 'email' | 'sms' | 'both') => {
    setRecipientContactMethods(prev => ({
      ...prev,
      [recipientId]: method
    }));
  };
  const toggleRecipientTotalView = (recipientId: number) => {
    setRecipientsViewTotalSettings(prev => ({
      ...prev,
      [recipientId]: !prev[recipientId]
    }));
  };
  // Calculate commission values
  const totalAmountNum = parseFloat(totalAmount) || 0;
  const totalCommission = suppliers.reduce((sum, s) => {
    if (commissionType === 'percentage') {
      return sum + totalAmountNum * (parseFloat(s.percentage || '0') / 100);
    }
    return sum + (parseFloat(s.amount) || 0);
  }, 0);
  const businessShare = totalAmountNum - totalCommission;
  const getModalTitle = () => {
    if (step === 'preview') {
      if (splitType === 'dates') return 'Payment schedule preview';
      if (splitType === 'commission') return 'Commission split preview';
    }
    if (hasMultipleRecipients) return 'Share payment link';
    return 'Share payment link';
  };
  // ─── SCHEDULE PREVIEW ───
  const renderSchedulePreview = () => <div className="space-y-4" data-id="element-3940">
      <p className="text-sm text-gray-600" data-id="element-3941">
        Review the payment schedule before creating the link.
      </p>

      <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-3942">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-3943">
          <div className="grid grid-cols-12 gap-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3944">
            <div className="col-span-1" data-id="element-3945">#</div>
            <div className="col-span-3" data-id="element-3946">Amount</div>
            <div className="col-span-3" data-id="element-3947">Due date</div>
            <div className="col-span-3" data-id="element-3948">Send link on</div>
            <div className="col-span-2" data-id="element-3949">Status</div>
          </div>
        </div>
        <div className="divide-y divide-gray-100" data-id="element-3950">
          {installments.map((inst, index) => <div key={inst.id} className="px-4 py-3.5" data-id="element-3951">
              <div className="grid grid-cols-12 gap-3 items-center" data-id="element-3952">
                <div className="col-span-1" data-id="element-3953">
                  <div className="w-6 h-6 flex items-center justify-center bg-[#F7941D]/10 text-[#F7941D] rounded-full font-bold text-xs" data-id="element-3954">
                    {index + 1}
                  </div>
                </div>
                <div className="col-span-3" data-id="element-3955">
                  <span className="text-sm font-semibold text-gray-900" data-id="element-3956">
                    THB {parseFloat(inst.amount || '0').toFixed(2)}
                  </span>
                  {inst.description && <p className="text-xs text-gray-500 mt-0.5" data-id="element-3957">
                      {inst.description}
                    </p>}
                </div>
                <div className="col-span-3" data-id="element-3958">
                  <div className="flex items-center text-sm text-gray-700" data-id="element-3959">
                    <CalendarIcon size={13} className="mr-1.5 text-gray-400" data-id="element-3960" />
                    {inst.dueDate || 'Not set'}
                  </div>
                </div>
                <div className="col-span-3" data-id="element-3961">
                  <div className="flex items-center text-sm text-gray-700" data-id="element-3962">
                    <SendIcon size={13} className="mr-1.5 text-gray-400" data-id="element-3963" />
                    {inst.sendLinkDate || 'Auto'}
                  </div>
                </div>
                <div className="col-span-2" data-id="element-3964">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-3965">
                    Pending
                  </span>
                </div>
              </div>
            </div>)}
        </div>
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200" data-id="element-3966">
          <div className="flex justify-between items-center" data-id="element-3967">
            <span className="text-sm font-medium text-gray-600" data-id="element-3968">Total</span>
            <span className="text-sm font-bold text-gray-900" data-id="element-3969">
              THB{' '}
              {installments.reduce((sum, i) => sum + parseFloat(i.amount || '0'), 0).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-[#F7941D]/5 border border-[#F7941D]/20 rounded-xl p-4" data-id="element-3970">
        <div className="flex items-start gap-3" data-id="element-3971">
          <CalendarIcon size={18} className="text-[#F7941D] mt-0.5 flex-shrink-0" data-id="element-3972" />
          <div data-id="element-3973">
            <p className="text-sm font-medium text-gray-900" data-id="element-3974">
              Automatic link delivery
            </p>
            <p className="text-xs text-gray-600 mt-0.5" data-id="element-3975">
              Payment links will be sent automatically on the scheduled dates.
              You can also share the master link manually.
            </p>
          </div>
        </div>
      </div>
    </div>;
  // ─── COMMISSION PREVIEW ───
  const renderCommissionPreview = () => <div className="space-y-4" data-id="element-3976">
      <p className="text-sm text-gray-600" data-id="element-3977">
        Review the commission breakdown before creating the payment link.
      </p>

      {/* Total amount card */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-5" data-id="element-3978">
        <div className="flex items-center justify-between mb-4" data-id="element-3979">
          <span className="text-sm font-medium text-gray-500" data-id="element-3980">
            Total payment amount
          </span>
          <span className="text-xl font-bold text-gray-900" data-id="element-3981">
            THB {totalAmountNum.toFixed(2)}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-3982">
          <div className="h-full rounded-full bg-gradient-to-r from-[#F7941D] to-[#F7941D]/70" style={{
          width: totalAmountNum > 0 ? `${Math.min(totalCommission / totalAmountNum * 100, 100)}%` : '0%'
        }} data-id="element-3983" />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500" data-id="element-3984">
          <span className="flex items-center gap-1" data-id="element-3985">
            <span className="w-2 h-2 rounded-full bg-[#F7941D]" data-id="element-3986" />
            Commission: THB {totalCommission.toFixed(2)}
          </span>
          <span className="flex items-center gap-1" data-id="element-3987">
            <span className="w-2 h-2 rounded-full bg-gray-300" data-id="element-3988" />
            Your share: THB {businessShare.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Supplier breakdown */}
      <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-3989">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-3990">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3991">
            Supplier commission breakdown
          </h4>
        </div>
        <div className="divide-y divide-gray-100" data-id="element-3992">
          {suppliers.map((supplier, index) => {
          const supplierAmount = commissionType === 'percentage' ? totalAmountNum * (parseFloat(supplier.percentage || '0') / 100) : parseFloat(supplier.amount) || 0;
          const supplierPercentage = commissionType === 'percentage' ? parseFloat(supplier.percentage || '0') : totalAmountNum > 0 ? supplierAmount / totalAmountNum * 100 : 0;
          return <div key={supplier.id} className="px-4 py-4" data-id="element-3993">
                <div className="flex items-center justify-between" data-id="element-3994">
                  <div className="flex items-center gap-3" data-id="element-3995">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#F7941D]/10 text-[#F7941D] rounded-full font-bold text-xs flex-shrink-0" data-id="element-3996">
                      {index + 1}
                    </div>
                    <div data-id="element-3997">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-3998">
                        {supplier.name || `Supplier ${index + 1}`}
                      </p>
                      {supplier.email && <p className="text-xs text-gray-500" data-id="element-3999">
                          {supplier.email}
                        </p>}
                    </div>
                  </div>
                  <div className="text-right" data-id="element-4000">
                    <p className="text-sm font-bold text-gray-900" data-id="element-4001">
                      THB {supplierAmount.toFixed(2)}
                    </p>
                    <p className="text-xs text-[#F7941D] font-medium" data-id="element-4002">
                      {supplierPercentage.toFixed(1)}% of total
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Your business share */}
        <div className="px-4 py-4 bg-gray-50 border-t border-gray-200" data-id="element-4003">
          <div className="flex items-center justify-between" data-id="element-4004">
            <div className="flex items-center gap-3" data-id="element-4005">
              <div className="w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-600 rounded-full flex-shrink-0" data-id="element-4006">
                <BuildingIcon size={14} data-id="element-4007" />
              </div>
              <div data-id="element-4008">
                <p className="text-sm font-semibold text-gray-900" data-id="element-4009">
                  Your business
                </p>
                <p className="text-xs text-gray-500" data-id="element-4010">
                  Remaining after commission
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-4011">
              <p className="text-sm font-bold text-gray-900" data-id="element-4012">
                THB {businessShare.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 font-medium" data-id="element-4013">
                {totalAmountNum > 0 ? (businessShare / totalAmountNum * 100).toFixed(1) : '0.0'}
                % of total
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
  // ─── MULTI-RECIPIENT SHARE VIEW ───
  const renderMultiRecipientShare = () => <>
      <div className="flex justify-between items-center mb-4" data-id="element-4014">
        <p className="text-gray-600 font-medium" data-id="element-4015">
          Send unique payment links to each recipient
        </p>
      </div>
      <div className="space-y-4 max-h-[50vh] overflow-y-auto" data-id="element-4016">
        {recipients.map((recipient, index) => <div key={recipient.id} className="border border-gray-200 rounded-xl p-4" data-id="element-4017">
            <div className="flex items-center mb-3" data-id="element-4018">
              <div className="w-10 h-10 rounded-full bg-[#F7941D]/10 flex items-center justify-center flex-shrink-0" data-id="element-4019">
                <UserIcon size={20} className="text-[#F7941D]" data-id="element-4020" />
              </div>
              <div className="ml-3" data-id="element-4021">
                <h4 className="font-medium text-gray-800" data-id="element-4022">
                  Recipient {index + 1}
                </h4>
                <p className="text-sm text-gray-500" data-id="element-4023">
                  {recipient.email || recipient.phone}
                </p>
              </div>
            </div>

            {/* Toggle */}
            <div className="mb-3" data-id="element-4024">
              <label className="flex items-center cursor-pointer" data-id="element-4025">
                <input type="checkbox" className="sr-only" checked={recipientsViewTotalSettings[recipient.id] || false} onChange={() => toggleRecipientTotalView(recipient.id)} data-id="element-4026" />
                <div className={`w-10 h-5 rounded-full transition-colors ${recipientsViewTotalSettings[recipient.id] ? 'bg-[#F7941D]' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-4027">
                  <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${recipientsViewTotalSettings[recipient.id] ? 'translate-x-5' : ''}`} data-id="element-4028" />
                </div>
                <span className="ml-2 text-sm text-gray-600" data-id="element-4029">
                  {recipientsViewTotalSettings[recipient.id] ? <span className="flex items-center" data-id="element-4030">
                      <DollarSignIcon size={14} className="mr-1" data-id="element-4031" />
                      Allow recipient to view the total and outstanding amount
                    </span> : <span className="flex items-center" data-id="element-4032">
                      <EyeOffIcon size={14} className="mr-1" data-id="element-4033" />
                      Recipient will only see their portion
                    </span>}
                </span>
              </label>
            </div>

            {/* Payment link */}
            <div className="mb-3" data-id="element-4034">
              <div className="flex" data-id="element-4035">
                <input type="text" className="flex-1 px-3 py-2 border border-gray-200 rounded-l-lg focus:ring-[#F7941D]/20 focus:border-[#F7941D] bg-gray-50 text-sm" value={`https://payment.example.com/pay/${recipient.id}-abc123${recipientsViewTotalSettings[recipient.id] ? '?showTotal=true' : ''}`} readOnly data-id="element-4036" />
                <button className="px-3 py-2 bg-[#F7941D] text-white rounded-r-lg hover:bg-[#D97B06] transition-colors flex items-center text-sm font-medium" onClick={() => handleCopyLink(recipient.id)} data-id="element-4037">
                  {copiedRecipientId === recipient.id ? <>
                      <CheckIcon size={14} className="mr-1" data-id="element-4038" />
                      Copied
                    </> : <>
                      <LinkIcon size={14} className="mr-1" data-id="element-4039" />
                      Copy
                    </>}
                </button>
              </div>
            </div>

            {/* Contact options */}
            {(recipient.email || recipient.phone) && <div className="mt-3" data-id="element-4040">
                <h5 className="text-xs font-medium text-gray-500 mb-2" data-id="element-4041">
                  Send payment link via:
                </h5>
                <div className="flex space-x-2" data-id="element-4042">
                  {recipient.email && recipient.phone && <>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'email' ? 'bg-[#F7941D]/10 text-[#F7941D] border border-[#F7941D]/30' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'email')} data-id="element-4043">
                        <MailIcon size={14} className="mr-1.5" data-id="element-4044" />
                        Email
                      </button>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'sms' ? 'bg-[#F7941D]/10 text-[#F7941D] border border-[#F7941D]/30' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'sms')} data-id="element-4045">
                        <SmartphoneIcon size={14} className="mr-1.5" data-id="element-4046" />
                        SMS
                      </button>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'both' ? 'bg-[#F7941D] text-white border border-[#F7941D]' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'both')} data-id="element-4047">
                        Both
                      </button>
                    </>}
                  {recipient.email && !recipient.phone && <button className="flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium bg-[#F7941D]/10 text-[#F7941D] border border-[#F7941D]/30 cursor-default" data-id="element-4048">
                      <MailIcon size={14} className="mr-1.5" data-id="element-4049" />
                      Email only
                    </button>}
                  {!recipient.email && recipient.phone && <button className="flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium bg-[#F7941D]/10 text-[#F7941D] border border-[#F7941D]/30 cursor-default" data-id="element-4050">
                      <SmartphoneIcon size={14} className="mr-1.5" data-id="element-4051" />
                      SMS only
                    </button>}
                </div>
              </div>}

            {/* Display contact info */}
            <div className="mt-3 text-sm space-y-1" data-id="element-4052">
              {(getContactMethod(recipient) === 'email' || getContactMethod(recipient) === 'both') && recipient.email && <div className="flex items-center text-gray-600" data-id="element-4053">
                    <MailIcon size={14} className="mr-1.5" data-id="element-4054" />
                    {recipient.email}
                  </div>}
              {(getContactMethod(recipient) === 'sms' || getContactMethod(recipient) === 'both') && recipient.phone && <div className="flex items-center text-gray-600" data-id="element-4055">
                    <SmartphoneIcon size={14} className="mr-1.5" data-id="element-4056" />
                    {recipient.phone}
                  </div>}
            </div>
          </div>)}
      </div>
    </>;
  // ─── STANDARD SHARE VIEW (single link) ───
  const renderStandardShare = () => <>
      <p className="text-gray-600 mb-4" data-id="element-4057">
        Choose how you want to share this payment link:
      </p>
      <div className="space-y-3" data-id="element-4058">
        {/* Email option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'email' ? 'border-[#F7941D] bg-[#F7941D]/[0.03] ring-1 ring-[#F7941D]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('email')} data-id="element-4059">
          <div className="flex items-center" data-id="element-4060">
            <div className="w-10 h-10 rounded-full bg-[#F7941D]/10 flex items-center justify-center flex-shrink-0" data-id="element-4061">
              <MailIcon size={20} className="text-[#F7941D]" data-id="element-4062" />
            </div>
            <div className="ml-4 flex-1" data-id="element-4063">
              <h4 className="font-medium text-gray-800" data-id="element-4064">Send via email</h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-4065">
                Send the payment link to their email address
              </p>
            </div>
          </div>
          {selectedOption === 'email' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-4066">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4067">
                Email address
              </label>
              <input type="email" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" placeholder="name@example.com" value={recipientInput} onChange={e => setRecipientInput(e.target.value)} data-id="element-4068" />
            </div>}
        </div>

        {/* SMS option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'sms' ? 'border-[#F7941D] bg-[#F7941D]/[0.03] ring-1 ring-[#F7941D]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('sms')} data-id="element-4069">
          <div className="flex items-center" data-id="element-4070">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0" data-id="element-4071">
              <SmartphoneIcon size={20} className="text-green-600" data-id="element-4072" />
            </div>
            <div className="ml-4 flex-1" data-id="element-4073">
              <h4 className="font-medium text-gray-800" data-id="element-4074">
                Send via text message
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-4075">
                Send the payment link to their phone
              </p>
            </div>
          </div>
          {selectedOption === 'sms' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-4076">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4077">
                Phone number
              </label>
              <input type="tel" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" placeholder="+1 (555) 123-4567" value={recipientInput} onChange={e => setRecipientInput(e.target.value)} data-id="element-4078" />
            </div>}
        </div>

        {/* QR Code option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'qrcode' ? 'border-[#F7941D] bg-[#F7941D]/[0.03] ring-1 ring-[#F7941D]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('qrcode')} data-id="element-4079">
          <div className="flex items-center" data-id="element-4080">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-4081">
              <QrCodeIcon size={20} className="text-amber-600" data-id="element-4082" />
            </div>
            <div className="ml-4 flex-1" data-id="element-4083">
              <h4 className="font-medium text-gray-800" data-id="element-4084">Generate QR code</h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-4085">
                Create a scannable QR code for the payment link
              </p>
            </div>
          </div>
          {selectedOption === 'qrcode' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-4086">
              <div className="flex flex-col items-center" data-id="element-4087">
                <div ref={qrCodeRef} className="bg-white p-4 rounded-lg mb-3" data-id="element-4088">
                  <QRCodeSVG value="https://payment.example.com/pay/abc123" size={200} level="H" includeMargin={true} data-id="element-4089" />
                </div>
                <button className="px-4 py-2 bg-[#F7941D] text-white rounded-lg hover:bg-[#D97B06] transition-colors flex items-center font-medium text-sm" onClick={handleDownloadQR} data-id="element-4090">
                  {qrDownloaded ? <>
                      <CheckIcon size={16} className="mr-2" data-id="element-4091" />
                      Downloaded
                    </> : <>
                      <DownloadIcon size={16} className="mr-2" data-id="element-4092" />
                      Download QR Code
                    </>}
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center" data-id="element-4093">
                  Customers can scan this QR code with their phone's camera to
                  open the payment link
                </p>
              </div>
            </div>}
        </div>

        {/* Copy link option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'copy' ? 'border-[#F7941D] bg-[#F7941D]/[0.03] ring-1 ring-[#F7941D]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('copy')} data-id="element-4094">
          <div className="flex items-center" data-id="element-4095">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-4096">
              <LinkIcon size={20} className="text-purple-600" data-id="element-4097" />
            </div>
            <div className="ml-4 flex-1" data-id="element-4098">
              <h4 className="font-medium text-gray-800" data-id="element-4099">Copy link to share</h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-4100">
                Copy the payment link to share yourself
              </p>
            </div>
          </div>
          {selectedOption === 'copy' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-4101">
              <div className="flex" data-id="element-4102">
                <input type="text" className="flex-1 px-3 py-2.5 border border-gray-200 rounded-l-lg focus:ring-[#F7941D]/20 focus:border-[#F7941D] bg-gray-50 text-sm" value="https://payment.example.com/pay/abc123" readOnly data-id="element-4103" />
                <button className="px-4 py-2.5 bg-[#F7941D] text-white rounded-r-lg hover:bg-[#D97B06] transition-colors flex items-center font-medium text-sm" onClick={() => handleCopyLink()} data-id="element-4104">
                  {copied ? <>
                      <CheckIcon size={16} className="mr-1" data-id="element-4105" />
                      Copied
                    </> : <>
                      <LinkIcon size={16} className="mr-1" data-id="element-4106" />
                      Copy
                    </>}
                </button>
              </div>
            </div>}
        </div>
      </div>
    </>;
  // ─── FOOTER BUTTONS ───
  const renderFooter = () => {
    if (step === 'preview') {
      return <div className="p-4 border-t border-gray-200 flex justify-between" data-id="element-4107">
          <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium" onClick={onClose} data-id="element-4108">
            Cancel
          </button>
          <button className="px-5 py-2.5 bg-[#F7941D] text-white rounded-lg hover:bg-[#D97B06] transition-colors flex items-center text-sm font-medium" onClick={() => setStep('share')} data-id="element-4109">
            Continue to share
            <ArrowRightIcon size={16} className="ml-2" data-id="element-4110" />
          </button>
        </div>;
    }
    // Share step
    const showBackButton = splitType === 'dates' || splitType === 'commission';
    return <div className="p-4 border-t border-gray-200 flex justify-between" data-id="element-4111">
        <div className="flex items-center gap-2" data-id="element-4112">
          {showBackButton && <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium flex items-center" onClick={() => setStep('preview')} data-id="element-4113">
              <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-4114" />
              Back
            </button>}
          <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium" onClick={onClose} data-id="element-4115">
            Cancel
          </button>
        </div>
        <div data-id="element-4116">
          {hasMultipleRecipients ? <button className="px-5 py-2.5 bg-[#F7941D] text-white rounded-lg hover:bg-[#D97B06] transition-colors flex items-center text-sm font-medium" onClick={handleSend} data-id="element-4117">
              <SendIcon size={16} className="mr-2" data-id="element-4118" />
              Send to all recipients
            </button> : <>
              {(selectedOption === 'email' || selectedOption === 'sms') && <button className="px-5 py-2.5 bg-[#F7941D] text-white rounded-lg hover:bg-[#D97B06] transition-colors flex items-center text-sm font-medium disabled:opacity-50" onClick={handleSend} disabled={!recipientInput} data-id="element-4119">
                  <SendIcon size={16} className="mr-2" data-id="element-4120" />
                  Send
                </button>}
              {(selectedOption === 'copy' || selectedOption === 'qrcode') && <button className="px-5 py-2.5 bg-[#F7941D] text-white rounded-lg hover:bg-[#D97B06] transition-colors text-sm font-medium" onClick={onClose} data-id="element-4121">
                  Done
                </button>}
              {!selectedOption && <button className="px-5 py-2.5 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed" disabled data-id="element-4122">
                  Select an option
                </button>}
            </>}
        </div>
      </div>;
  };
  return createPortal(<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" data-id="element-4123">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 overflow-hidden" data-id="element-4124">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-4125">
          <div className="flex items-center gap-3" data-id="element-4126">
            {step === 'preview' && splitType === 'dates' && <div className="w-8 h-8 rounded-lg bg-[#F7941D]/10 flex items-center justify-center" data-id="element-4127">
                <CalendarIcon size={16} className="text-[#F7941D]" data-id="element-4128" />
              </div>}
            {step === 'preview' && splitType === 'commission' && <div className="w-8 h-8 rounded-lg bg-[#F7941D]/10 flex items-center justify-center" data-id="element-4129">
                <PercentIcon size={16} className="text-[#F7941D]" data-id="element-4130" />
              </div>}
            <div data-id="element-4131">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-4132">
                {getModalTitle()}
              </h3>
              {step === 'preview' && <div className="flex items-center gap-2 mt-1" data-id="element-4133">
                  <div className="flex items-center gap-1" data-id="element-4134">
                    <div className="w-5 h-5 rounded-full bg-[#F7941D] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-4135">
                      1
                    </div>
                    <span className="text-xs font-medium text-[#F7941D]" data-id="element-4136">
                      Review
                    </span>
                  </div>
                  <div className="w-4 h-px bg-gray-300" data-id="element-4137" />
                  <div className="flex items-center gap-1" data-id="element-4138">
                    <div className="w-5 h-5 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-[10px] font-bold" data-id="element-4139">
                      2
                    </div>
                    <span className="text-xs text-gray-400" data-id="element-4140">Share</span>
                  </div>
                </div>}
              {step === 'share' && (splitType === 'dates' || splitType === 'commission') && <div className="flex items-center gap-2 mt-1" data-id="element-4141">
                    <div className="flex items-center gap-1" data-id="element-4142">
                      <div className="w-5 h-5 rounded-full bg-[#F7941D] text-white flex items-center justify-center" data-id="element-4143">
                        <CheckIcon size={10} data-id="element-4144" />
                      </div>
                      <span className="text-xs text-gray-400" data-id="element-4145">Review</span>
                    </div>
                    <div className="w-4 h-px bg-[#F7941D]" data-id="element-4146" />
                    <div className="flex items-center gap-1" data-id="element-4147">
                      <div className="w-5 h-5 rounded-full bg-[#F7941D] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-4148">
                        2
                      </div>
                      <span className="text-xs font-medium text-[#F7941D]" data-id="element-4149">
                        Share
                      </span>
                    </div>
                  </div>}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors" data-id="element-4150">
            <XIcon size={20} data-id="element-4151" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[65vh] overflow-y-auto" data-id="element-4152">
          {step === 'preview' && splitType === 'dates' && renderSchedulePreview()}
          {step === 'preview' && splitType === 'commission' && renderCommissionPreview()}
          {step === 'share' && hasMultipleRecipients && renderMultiRecipientShare()}
          {step === 'share' && !hasMultipleRecipients && renderStandardShare()}
        </div>

        {/* Footer */}
        {renderFooter()}
      </div>
    </div>, document.body);
}